import {
  Badge,
  Box,
  Button,
  Card,
  Group,
  MantineGradient,
  Modal,
  Stack,
  Text,
  createStyles,
  getStylesRef,
  useMantineTheme,
} from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import React from 'react'
import { InfoCircle } from 'tabler-icons-react'
import { PortfolioModalContent } from '../PortfolioModalContent'
import { transitionStyles } from 'src/styles'
import Fade from 'react-reveal/Fade'

export interface PortfolioCardProps {
  name: string
  description: string
  technologies: string[]
  gradient?: MantineGradient
  image: string
  link: string
  detailedInfo: string[]
}

const useStyles = createStyles((theme, { gradient, isMobile }: any) => ({
  title: {
    fontSize: theme.fontSizes.lg,
    color: theme.colorScheme === 'dark' ? theme.colors.gray[2] : theme.colors.gray[7],
    fontWeight: 600,
  },
  card: {
    boxShadow: theme.shadows.lg,
    borderRadius: isMobile ? 15 : 0,
    borderTopLeftRadius: 15,
    borderBottomLeftRadius: 15,

    [`&:hover .${getStylesRef('infoButton')}`]: {
      backgroundColor: theme.colorScheme === 'dark' ? '#26354a' : '#d9edfc',
      transition: transitionStyles.themeChange,
      border: 'none',
    },
  },
  infoButton: {
    ref: getStylesRef('infoButton'),
    marginRight: 10,
    height: 80,
    width: 80,
    borderRadius: '50%',
  },
  boxGradientStyle: { backgroundImage: theme.fn.gradient(gradient) },
}))

export const PortfolioCard: React.FC<PortfolioCardProps> = props => {
  const { name, description, technologies, gradient } = props
  const [opened, { open, close }] = useDisclosure(false)

  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.md})`)
  const { classes } = useStyles({ gradient, isMobile })

  return (
    <Box w={isMobile ? '80%' : '100%'}>
      <Fade bottom>
        <Card p={isMobile ? 'sm' : 0} radius={0} className={classes.card} onClick={open}>
          <Group position={'apart'}>
            <Group>
              {!isMobile && <Box w={50} h={130} className={classes.boxGradientStyle} />}
              <Stack align="flex-start" spacing={'sm'}>
                <Text className={classes.title}>{name}</Text>
                <Text size="sm">{description}</Text>
                <Group mt={4} sx={{ display: 'flex', justifyContent: isMobile ? 'center' : 'flex-start' }}>
                  {technologies?.map((tech, index) => (
                    <Badge
                      sx={{
                        cursor: 'pointer',
                      }}
                      w={'min-content'}
                      key={index}
                      variant={gradient ? 'gradient' : 'outline'}
                      gradient={gradient}
                    >
                      <Text size={'xs'}>{tech}</Text>
                    </Badge>
                  ))}
                </Group>
              </Stack>
            </Group>
            {!isMobile && (
              <Group>
                <Stack align="center" justify={'center'}>
                  <Button className={classes.infoButton} variant="subtle" onClick={open}>
                    <InfoCircle size={30} />
                  </Button>
                </Stack>
                <Box w={8} h={130} className={classes.boxGradientStyle} />
              </Group>
            )}
          </Group>
        </Card>
      </Fade>
      <Modal
        opened={opened}
        onClose={close}
        transitionProps={{ transition: 'fade', duration: 600, timingFunction: 'linear' }}
        overlayProps={{
          color: theme.colorScheme === 'dark' ? theme.colors.dark[9] : theme.colors.gray[2],
          opacity: 0.8,
          blur: 4,
        }}
        withCloseButton={isMobile}
        radius={'md'}
        title={isMobile ? name : ''}
        centered
      >
        <PortfolioModalContent close={close} {...props} />
      </Modal>
    </Box>
  )
}
